import { supabase } from '../lib/supabase';

/**
 * Invoke a Supabase edge function with the given JSON payload.
 *
 * Previously this called an AWS Lambda URL via axios. All AI features now
 * route through Supabase edge functions (`chat-completion`, etc.) so the
 * browser never talks to an external provider directly and we can enforce
 * auth + rate limiting server-side. The caller signature is unchanged — we
 * just reinterpret the first argument as an edge-function slug instead of a
 * full URL, and let supabase-js attach the user's JWT automatically.
 *
 * @param {string} slug    - Edge function slug (e.g. 'chat-completion').
 * @param {object} payload - JSON body forwarded to the function.
 * @returns {Promise<object>} The function's response body.
 */
export async function callLambdaFunction(slug, payload) {
  const { data, error } = await supabase?.functions?.invoke(slug, { body: payload });
  if (error) {
    // Surface the function's error body when available so callers see the
    // real reason (rate-limit hit, OpenAI error, etc.) instead of a generic
    // "Function returned a non-2xx status code".
    let details = error?.message || 'Edge function call failed';
    let status;
    try {
      const ctx = error?.context;
      // FunctionsHttpError attaches the raw Response on .context — both
      // .status (HTTP code) and .json() body are available there.
      if (ctx) {
        status = ctx?.status;
        if (typeof ctx?.json === 'function') {
          const body = await ctx.json();
          if (body?.error) details = body.error;
        }
      }
    } catch (_) { /* ignore JSON parse errors */ }
    // Status-aware copy: 429 (rate limit) and 5xx (transient) get distinct
    // user-facing hints. The body's `error` (when present) wins for 4xx.
    let friendlyMsg = details;
    if (status === 429) {
      friendlyMsg = `${slug}: service is busy, please retry in a moment. (${details})`;
    } else if (typeof status === 'number' && status >= 500) {
      friendlyMsg = `${slug}: service temporarily unavailable, please retry shortly. (${details})`;
    }
    console.error('Edge function error:', { slug, status, details });
    const e = new Error(friendlyMsg);
    e.status = status;
    throw e;
  }
  return data;
}
